/**
 * AdminTenants.tsx — lista tenantow dla panelu super-admina (F7a T7.1, T8.4b).
 *
 * Model filtrowania „jak w katalogu modulow" (poprawki-develop): ZERO gornej
 * wyszukiwarki i gornych dropdownow filtrow — cale filtrowanie zyje w naglowkach
 * kolumn (wspoldzielony ConfigurableTable, client-side na pobranej liscie).
 * Kolumny wolnotekstowe (Kod/Slug/Nazwa/Opiekun/Moduly) maja szukajke; Status i
 * Tryb DB — dropdown wyboru. Nad tabela zostaja tylko kontrolki, ktore ZMIENIAJA
 * pobierany zbior i nie maja odpowiednika w kolumnie: „Trial konczy sie w (dni)"
 * i „Moi podopieczni" (analogicznie do „Pokaz wycofane" w katalogu modulow).
 * Pobieramy do gornego limitu (100) — filtry per kolumna obejmuja pobrana liste.
 *
 * Zaznaczanie i pasek akcji masowych bez zmian (CT wlada checkboxami przez
 * `selectable`).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';

import { AdminTenantsBulkModal } from '@/components/AdminTenantsBulkModal';
import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { InfoHint } from '@/components/InfoHint';
import { Input } from '@/components/Input';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { TenantCaretakerManageModal } from '@/components/tenant-detail/TenantCaretakerManageModal';
import { useAuth } from '@/contexts/AuthContext';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { api } from '@/lib/api';

import { CreateTenantModal } from './CreateTenantModal';

export interface TenantListItem {
  id: string;
  code: string;
  slug: string;
  name: string;
  dbMode: 'shared' | 'dedicated';
  dbStatus: string;
  createdAt: string;
  gcrecord: string | null;
  subscriptions: Array<{
    moduleCode: string;
    moduleName: string;
    status: string | null;
    trialEndsAt: string | null;
    activatedAt: string | null;
  }>;
  caretakers: Array<{
    adminUserId: string;
    adminName: string | null;
    adminEmail: string;
  }>;
}

interface TenantListResponse {
  tenants: TenantListItem[];
  total: number;
  limit: number;
  offset: number;
}

type BulkAction = 'suspend' | 'activate' | 'extend_trial';

/** Gorny limit pobrania — pod client-side filtry per kolumna (route capuje na 100). */
const LIST_FETCH_LIMIT = 100;

/** Kolejnosc statusow subskrypcji — opcje filtra select w kolumnie „Status". */
const SUBSCRIPTION_STATUSES = ['trialing', 'active', 'past_due', 'cancelled', 'suspended'] as const;

const STATUS_TO_COLOR: Record<string, BadgeColor> = {
  active: 'green',
  trialing: 'blue',
  past_due: 'amber',
  cancelled: 'gray',
  suspended: 'red',
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function AdminTenants() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canManageCaretakers = hasPermission('tenants.caretakers.manage');

  // Zarządzanie opiekunem wprost z listy (klik komórki „Opiekun").
  const [manageCaretakerTenantId, setManageCaretakerTenantId] = useState<string | null>(null);

  // Kontrolki, które zmieniają pobierany zbiór (bez odpowiednika w kolumnie).
  const [trialEndsInInput, setTrialEndsInInput] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);

  const trialEndsInValid =
    trialEndsInInput === '' ||
    (Number.isFinite(Number(trialEndsInInput)) &&
      Number(trialEndsInInput) >= 1 &&
      Number(trialEndsInInput) <= 365);

  const { data, isLoading, isError, error } = useQuery<TenantListResponse>({
    queryKey: [
      'admin',
      'tenants',
      {
        trialEndsIn: trialEndsInValid && trialEndsInInput ? trialEndsInInput : '',
        mine: mineOnly,
      },
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (trialEndsInValid && trialEndsInInput) params.set('trialEndsIn', trialEndsInInput);
      if (mineOnly) params.set('mine', 'true');
      params.set('limit', String(LIST_FETCH_LIMIT));
      const qs = params.toString();
      const res = await api.get<TenantListResponse>(`/admin/tenants${qs ? '?' + qs : ''}`);
      return res.data;
    },
  });

  const tenants = useMemo<TenantListItem[]>(() => data?.tenants ?? [], [data]);

  const hasAnyFilter = !!trialEndsInInput || mineOnly;

  const clearAllFilters = () => {
    setTrialEndsInInput('');
    setMineOnly(false);
  };

  // Selekcja resetuje sie gdy zmienia sie pobierany zbior.
  useEffect(() => {
    setSelectedIds([]);
  }, [trialEndsInInput, mineOnly]);

  const statusLabel = useCallback(
    (status: string | null): string => {
      if (!status) return '—';
      const key = `admin.tenants.subscriptionStatus.${status}`;
      const translated = t(key);
      return translated === key ? status : translated;
    },
    [t],
  );

  const statusFilterOptions = useMemo(
    () => SUBSCRIPTION_STATUSES.map((s) => ({ value: s, label: statusLabel(s) })),
    [statusLabel],
  );

  const columns = useMemo<ColumnDefinition<TenantListItem>[]>(
    () => [
      {
        id: 'code',
        accessor: 'code',
        header: t('admin.tenants.columns.code'),
        sortable: true,
        renderCell: (value) => <span className="font-mono text-xs">{String(value)}</span>,
      },
      {
        id: 'slug',
        accessor: 'slug',
        header: t('admin.tenants.columns.slug'),
        sortable: true,
        renderCell: (value) => <span className="text-text-primary">{String(value)}</span>,
      },
      {
        id: 'name',
        accessor: 'name',
        header: t('admin.tenants.columns.name'),
        sortable: true,
        renderCell: (_value, row) => (
          <span className="font-medium text-text-primary">
            {row.name}
            {row.gcrecord && (
              <Badge color="gray" className="ml-2">
                {t('admin.tenants.deletedBadge')}
              </Badge>
            )}
          </span>
        ),
      },
      {
        id: 'status',
        accessor: (row) => row.subscriptions[0]?.status ?? '',
        header: t('admin.tenants.columns.status'),
        sortable: true,
        // Stany rozłączne → dropdown wyboru w wierszu filtrów (client-side, exact-match).
        headerFilter: { category: 'select', options: statusFilterOptions },
        renderCell: (_value, row) => {
          const primary = row.subscriptions[0];
          if (!primary) return <span className="text-text-muted">—</span>;
          const color = STATUS_TO_COLOR[primary.status ?? ''] ?? 'gray';
          return <Badge color={color}>{statusLabel(primary.status)}</Badge>;
        },
      },
      {
        id: 'caretaker',
        accessor: (row) => {
          const c = (row.caretakers ?? [])[0];
          return c?.adminName ?? c?.adminEmail ?? '';
        },
        header: t('admin.tenants.columns.caretaker'),
        sortable: true,
        renderCell: (_value, row) => {
          const list = row.caretakers ?? [];
          const c = list[0];
          const extra = list.length - 1;
          const label = c ? (
            <>
              {c.adminName || c.adminEmail}
              {extra > 0 && <span className="ml-1 text-text-muted">+{extra}</span>}
            </>
          ) : (
            <span className="text-text-muted">{t('admin.tenants.caretakerNone')}</span>
          );
          if (!canManageCaretakers) return <span className="text-text-secondary">{label}</span>;
          // Klik otwiera modal zarządzania opiekunem tego tenanta.
          return (
            <button
              type="button"
              className="cursor-pointer text-left text-text-secondary underline decoration-dotted underline-offset-2 hover:text-accent-600"
              title={t('admin.tenants.detail.caretaker.manageButton')}
              onClick={(e) => {
                e.stopPropagation();
                setManageCaretakerTenantId(row.id);
              }}
            >
              {label}
            </button>
          );
        },
      },
      {
        id: 'dbMode',
        accessor: (row) => row.dbMode,
        header: t('admin.tenants.columns.dbMode'),
        sortable: true,
        // Dwa tryby → dropdown wyboru (client-side).
        headerFilter: {
          category: 'select',
          options: [
            { value: 'shared', label: t('admin.tenants.filters.dbModeShared') },
            { value: 'dedicated', label: t('admin.tenants.filters.dbModeDedicated') },
          ],
        },
        renderCell: (_value, row) => (
          <Badge color={row.dbMode === 'dedicated' ? 'accent' : 'gray'}>{row.dbMode}</Badge>
        ),
      },
      {
        id: 'modules',
        // Kody modulow zlaczone spacja — szukajka tekstowa dziala substring po kodach.
        accessor: (row) => row.subscriptions.map((s) => s.moduleCode).join(' '),
        header: t('admin.tenants.columns.modules'),
        sortable: false,
        renderCell: (_value, row) =>
          row.subscriptions.length === 0 ? (
            <span className="text-text-muted">—</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {row.subscriptions.map((s) => (
                <Badge key={s.moduleCode} color="gray">
                  {s.moduleCode}
                </Badge>
              ))}
            </div>
          ),
      },
      {
        id: 'createdAt',
        accessor: 'createdAt',
        header: t('admin.tenants.columns.createdAt'),
        sortable: true,
        // Data — bez szukajki (fraza nie ma sensu).
        filterable: false,
        renderCell: (_value, row) => (
          <span className="text-text-secondary">{formatDate(row.createdAt)}</span>
        ),
      },
    ],
    [t, statusLabel, statusFilterOptions, canManageCaretakers],
  );

  const renderActions = useCallback(
    (row: TenantListItem) => (
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/tenants/${row.id}`);
        }}
      >
        {t('admin.tenants.details')}
      </Button>
    ),
    [navigate, t],
  );

  const selectedTenants = useMemo(
    () => tenants.filter((x) => selectedIds.includes(x.id)),
    [tenants, selectedIds],
  );

  return (
    <Layout title={t('admin.tenants.title')}>
      <section className="px-6 py-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-text-primary">{t('admin.tenants.title')}</h2>
            <p className="mt-1 text-sm text-text-tertiary">{t('admin.tenants.subtitle')}</p>
          </div>
          <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
            {t('admin.tenants.addButton')}
          </Button>
        </div>

        {/* Kontrolki zmieniajace pobierany zbior (reszta filtrow zyje w naglowkach kolumn). */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <Input
            label={t('admin.tenants.filters.trialEndsIn')}
            type="number"
            min={1}
            max={365}
            placeholder={t('admin.tenants.filters.trialEndsInPlaceholder')}
            value={trialEndsInInput}
            onChange={(e) => setTrialEndsInInput(e.target.value)}
            error={!trialEndsInValid ? t('admin.tenants.filters.trialEndsInInvalid') : undefined}
            containerClassName="sm:w-44"
          />
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-text-secondary">
            <Checkbox checked={mineOnly} onChange={() => setMineOnly((v) => !v)} />
            {t('admin.tenants.filters.mineOnly')}
          </label>
          <div className="max-w-sm self-end">
            <InfoHint>{t('admin.tenants.filters.mineOnlyHint')}</InfoHint>
          </div>
          {hasAnyFilter && (
            <Button variant="ghost" size="sm" onClick={clearAllFilters} className="self-end">
              {t('admin.tenants.filters.clearAll')}
            </Button>
          )}
        </div>

        {selectedIds.length > 0 && (
          <div
            role="region"
            aria-label={t('admin.tenants.bulk.toolbarLabel')}
            className="sticky top-2 z-10 mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-accent-300 bg-accent-50 px-4 py-3 shadow-sm dark:border-accent-700 dark:bg-accent-900/30"
          >
            <span className="text-sm font-medium text-text-primary">
              {t('admin.tenants.bulk.selectedCount', { count: selectedIds.length })}
            </span>
            <Button variant="secondary" size="sm" onClick={() => setBulkAction('suspend')}>
              {t('admin.tenants.bulk.suspend')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setBulkAction('activate')}>
              {t('admin.tenants.bulk.activate')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setBulkAction('extend_trial')}>
              {t('admin.tenants.bulk.extendTrial')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
              {t('admin.tenants.bulk.clear')}
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={48} />
            ))}
          </div>
        ) : isError ? (
          <div className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
            {t('admin.tenants.loadError')}
            {error instanceof Error && <span className="ml-1 opacity-70">({error.message})</span>}
          </div>
        ) : (
          <>
            <PreviewTenantAuthBoundary>
              <ConfigurableTable<TenantListItem>
                tableId="admin-tenants"
                testIdPrefix="admin-tenants"
                columns={columns}
                data={tenants}
                getRowId={(row) => row.id}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                renderActions={renderActions}
                persistConfig={false}
                showViewModeToggle={false}
                emptyIcon={Building2}
                emptyTitle={
                  hasAnyFilter ? t('admin.tenants.emptyFiltered') : t('admin.tenants.emptyNoData')
                }
                emptyDescription=""
                onClearAllFilters={clearAllFilters}
              />
            </PreviewTenantAuthBoundary>
            {data && (
              <p className="mt-3 text-xs text-text-muted">
                {data.total} tenantów
                {data.total > data.tenants.length &&
                  ` (wyświetlono ${data.tenants.length} z ${data.total})`}
              </p>
            )}
          </>
        )}
      </section>

      <CreateTenantModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={() => {
          setIsCreateOpen(false);
          void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
        }}
      />

      {canManageCaretakers && (
        <TenantCaretakerManageModal
          tenantId={manageCaretakerTenantId}
          open={manageCaretakerTenantId !== null}
          onClose={() => setManageCaretakerTenantId(null)}
        />
      )}

      {bulkAction && (
        <AdminTenantsBulkModal
          action={bulkAction}
          tenants={selectedTenants}
          onClose={() => setBulkAction(null)}
          onDone={() => {
            setBulkAction(null);
            setSelectedIds([]);
            void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
          }}
        />
      )}
    </Layout>
  );
}
